import { Brand, Embroidery, I18NText, Model, Personal, Position, State } from 'src/types'
import ko from 'src/locales/ko'
import { emojiChecker } from '../hooks'
import { usModelOfAtomsChecker } from 'src/features/brands/atoms/helper'

export const calculatorOfBaseCost = (baseModel: Model, seriesOrKindsValue: string): number => {
  switch (baseModel.brand) {
    case 'atoms': {
      const seriesPriceKey = ['soft'].includes(seriesOrKindsValue) ? 'softPrice' : 'price'
      return baseModel[seriesPriceKey]
    }
    case 'yellStory': {
      switch (seriesOrKindsValue) {
        case 'newBestSelect':
          return 69300 // 税込
        case 'newGoldLineNext':
          return 66000 // 税込
        case 'newJuniorLine':
          return 55000 // 税込
        default:
          return baseModel?.price
      }
    }
    case 'ragdelion': {
      switch (seriesOrKindsValue) {
        case 'japanSteer':
          return baseModel['japanSteerPrice'] // 2024年8月以降は廃止
        case 'jLeather':
          return baseModel['jLeatherPrice'] // 2024年8月以降は廃止
        case 'ugLeather':
          return baseModel['ugLeatherPrice'] // 2024年8月以降は廃止
        case 'semiOrder':
          return baseModel['semiOrderPrice']
        default:
          return baseModel?.price // fullOrder / 2024年8月から
      }
    }
    case 'treasure': {
      const seriesPriceKey = ['soft'].includes(seriesOrKindsValue) ? 'treasureSoftPrice' : 'price'
      return baseModel[seriesPriceKey]
    }
    case 'gorilla': {
      const seriesPriceKey = seriesOrKindsValue === 'bomber' ? 'bomberPrice' : 'price'
      return baseModel[seriesPriceKey]
    }
    default: {
      return baseModel?.price || 0
    }
  }
}

export const calculatorOfOptionCost = (brand: Brand, position: Position, state: State): number => {
  const objectsOfLeatherColorParts = [
    // 'all',
    'catchFace', // 捕球面
    'web', // ウェブ
    'thumbHook', // 掛け紐
    'littleHook', // 掛け紐
    'indexCover', // 指カバー
    'thumbOut', // 親指外側
    'thumbWeb', // 親指内側
    'thumbIndex', // 親指・人差し指
    'thumbMachi', // キャッチャーミットの親指マチ
    'littleMachi', // キャッチャーミットの小指マチ
    'thumbWebAndIndexWeb', // Uバックの親指・人差し指
    'thumbIndexMiddleRight', // 親指・人差し指・中指右
    'indexWeb', // 人差し指右
    'indexMiddle', // 人差し指左
    'indexAndMiddle', //人差し指・中指
    'indexLeftMiddleRight', // 人差し指左・中指右
    'middleIndex', //中指右
    'middleRing', //中指左
    'middleLeftRingRight', // 中指左・薬指右
    'middleLeftRing', //  中指左・薬指
    'middleLeftRingLittle', //  中指左・薬指・小指
    'ringMiddle', // 薬指右
    'ringLittle', // 薬指左
    'ringLeftLittleRight', // 薬指左・小指右
    'littleRing', // 小指右
    'littleOut', //小指左
    'thumb', // 親指
    'boomerang', // ブーメラン
    'underWeb', // ファーストミットウェブ下
    'fingerStand', // キャッチャーミット台
    'listBelt', // ベルト
    'treasureOriginalParts'
  ].filter((parts) => state[parts]?.value !== 'unselected')
  switch (brand) {
    case 'atoms': {
      const sizeChangedCost = state?.size?.price || 0 // 指サイズのオプション
      const moldingOrderCost = state?.moldingOrder?.price || 0
      const loopOfRingFingerCost = state?.loopOfRingFinger?.price || 0
      const optionTotal = sizeChangedCost + moldingOrderCost + loopOfRingFingerCost
      return optionTotal
    }
    case 'yellStory': {
      const sizeChangedCost = state.size.price
      const isleatherIntegratedIndex = state.leatherIntegratedIndex.value === 'atIndexFinger'
      const isleatherIntegratedMiddle = state.leatherIntegratedMiddle.value === 'atMiddleFinger'
      const isleatherIntegratedRing = state.leatherIntegratedRing.value === 'atRingFinger'
      // 革一体形状費
      const numberOfLeatherIntegrateds = [isleatherIntegratedIndex, isleatherIntegratedMiddle, isleatherIntegratedRing].filter(Boolean)?.length || 0
      const leatherIntegratedCost =
        numberOfLeatherIntegrateds === 1 ? 3300 : numberOfLeatherIntegrateds === 2 ? 4950 : numberOfLeatherIntegrateds === 3 ? 6600 : 0

      // 三色以上、革カラーを指定した時の数
      const leatherColors = objectsOfLeatherColorParts.map((x) => state[x]?.color).filter(Boolean)
      const numberOfLeatherColors = new Set(leatherColors).size
      const multiLeatherColorsCost = numberOfLeatherColors <= 2 ? 0 : 3300 * (numberOfLeatherColors - 2)
      const optionTotalCost = sizeChangedCost + leatherIntegratedCost + multiLeatherColorsCost
      return optionTotalCost
    }
    case 'dxm': {
      // 2024年9月には priceを参照させたい。後方互換のために、 value参照を優先
      const loopOfRingFingerCost = state?.loopOfRingFinger?.value === 'loopOfRingFinger' ? 3300 : 0
      const hamidashiCutCost = state.hamidashiCut.value === 'hamidashiCut' ? 3300 : 0
      const optionTotalCost = loopOfRingFingerCost + hamidashiCutCost
      return optionTotalCost
    }
    case 'ragdelion': {
      // [薬指リング]___キャッチャーミット or なし ならば 0円
      const loopOfRingFingerCost = state?.loopOfRingFinger?.price || 0
      // 指の長さ変更
      const fingerSizeChangedCost = state?.size?.price || 0
      // [親指一体タイプ]__
      const thumbIntegratedCost = state?.beltType.price || 0
      // [背面紐]___ ミット or なし ならば 0円
      const backLaceCost = state.backLace?.price || 0
      //
      const leatherIntegratedMiddleCost = state.leatherIntegratedMiddle?.price || 0
      // ライオンロゴ
      const optionTotalCost = loopOfRingFingerCost + backLaceCost + thumbIntegratedCost + fingerSizeChangedCost + leatherIntegratedMiddleCost
      return optionTotalCost
    }
    case 'treasure': {
      // グラブのサイズ
      const sizeChangedCost = state.size?.price || 0
      const isOmitFingerGuardParts = state.fingerGuardType.value === 'none'
      const objectsOfleatherColorParts2 = isOmitFingerGuardParts
        ? objectsOfLeatherColorParts.filter((x) => x !== 'indexCover')
        : objectsOfLeatherColorParts
      const isOmitTreasureOriginalParts = state.beltType.value !== 'treasureOriginal'
      const objectsOfleatherColorParts3 = isOmitTreasureOriginalParts
        ? objectsOfleatherColorParts2.filter((x) => x !== 'treasureOriginalParts')
        : objectsOfleatherColorParts2

      const isOmitBandParts = ['treasureOriginal', 'thumbIntegrated'].includes(state.beltType.value)
      const objectsOfleatherColorParts4 = isOmitBandParts ? objectsOfleatherColorParts3.filter((x) => x !== 'listBelt') : objectsOfleatherColorParts3

      // 三色以上、革カラーを指定した時の数
      const leatherColors = objectsOfleatherColorParts4.map((x) => state[x]?.color).filter(Boolean)
      const numberOfLeatherColors = new Set(leatherColors).size
      const multiLeatherColorsCost = numberOfLeatherColors <= 2 ? 0 : 3300 * (numberOfLeatherColors - 2)
      const optionTotalCost = sizeChangedCost + multiLeatherColorsCost
      return optionTotalCost
    }
    case 'gorilla': {
      // グラブのサイズ
      const sizeChangedCost = state.size?.price || 0
      const labelCost = state.gorillaLabel?.price || 0
      const optionTotalCost = sizeChangedCost + labelCost
      return optionTotalCost || 0
    }
  }
}

export const calculatorOfEmbroideryCost = (brand: Brand, t: I18NText, isOrderFromKorea: boolean, state: State): number => {
  const embroideries = state.embroideries
  const embroideriesOfFiltered = embroideries.filter((e) => e.content.trim()?.length > 0)
  switch (brand) {
    case 'atoms': {
      // 裏革1段目と2段目の刺繍をフィルタリング(位置のフィルタリング)
      const unitPricer = (e?: Embroidery) => {
        const isEdge = e?.edgeColor?.value !== 'none' //フチ付きかどうか
        const isShadow = e?.shadowColor?.value !== 'none' //カゲ付きかどうか
        if (e === undefined) {
          return 0
        }
        const price = isEdge ? t.EDGE_COLOR_PRICE : isShadow ? t.SHADOW_COLOR_PRICE : t.MAIN_COLOR_PRICE
        return price
      }
      const isUsModel = usModelOfAtomsChecker(state.baseModel?.productNumber)
      const costsOfEmbroideries = embroideriesOfFiltered.map((e) => unitPricer(e)) // [2750, 4400, ...]

      if (!isUsModel) {
        const maxCost = Math.max(...costsOfEmbroideries)
        const existLiningFirst = embroideriesOfFiltered.some((e) => ['leatherLiningFirst'].includes(e.position?.value))
        const excludePrice = unitPricer(embroideriesOfFiltered.find((x) => x.position.value === 'leatherLiningFirst'))
        const discountPriceFirst = isOrderFromKorea ? maxCost : existLiningFirst ? excludePrice : 0
        const existLiningSecond = embroideriesOfFiltered.some((e) => ['leatherLiningSecond'].includes(e.position?.value))
        const excludePriceSecond = unitPricer(embroideriesOfFiltered.find((x) => x.position.value === 'leatherLiningSecond'))
        const discountPrice = existLiningSecond ? excludePriceSecond + discountPriceFirst : discountPriceFirst
        const total = costsOfEmbroideries.reduce((a, c) => a + c, 0) - discountPrice // 7150
        return total
      } else {
        // const totalUs = costsOfEmbroideries.reduce((a, c) => a + c, 0)
        // USモデルなら、絵文字の追加料金を計算してreturn
        const additionalEmojiCost = embroideriesOfFiltered.reduce((a, c) => {
          const existEmoji = emojiChecker(c.content)
          if (!existEmoji) return a
          // 絵文字が存在する↓
          const emojiCount = c.content.match(/(🩷|⭐️|♥)/g)?.length || 0
          a += t.MAIN_COLOR_PRICE * emojiCount
          return a
        }, 0)
        const emojiExistBetweenCharCost = embroideriesOfFiltered.reduce((a, c) => {
          const existEmoji = emojiChecker(c.content)
          if (!existEmoji) return a

          // 絵文字が存在する↓
          const separateCount = c.content.split(/(🩷|⭐️|♥)/g).filter((str) => !['🩷', '⭐️', '♥'].includes(str) && str.length > 0).length
          a += unitPricer(c) * separateCount
          return a
        }, 0)

        // MEMO 2024/07/09 にUSモデルはdiscountなしに変更
        // MEMO 2025/03/20 にUSモデルは通常料金の加算をしない方針に変更
        const usTotal = additionalEmojiCost + emojiExistBetweenCharCost // + totalUs
        return usTotal > 0 ? usTotal : 0
      }
    }
    case 'yellStory': {
      // contentが入力されているもののみ抽出
      const costsOfEmbroideries = embroideriesOfFiltered.map((e) => {
        const isEdge = e.edgeColor.value !== 'none' //フチ付きかどうか
        const isShadow = e.shadowColor.value !== 'none' //カゲ付きかどうか
        const cost = isEdge ? 4620 : isShadow ? 2970 : 2420
        return cost // 2750
      }) // [2750, 4400, ...]

      // 一番高い刺繍をサービスで無料にする
      const discountPrice = costsOfEmbroideries.length ? Math.max(...costsOfEmbroideries) : 0

      const originalEmbroideryCost = state.ysrOriginalEmbroidery?.price || 0
      const directLogo1Cost = state.ysrDirectLogo1Position?.price || 0
      const directLogo2Cost = state.ysrDirectLogo2Position?.price || 0
      const total = costsOfEmbroideries.reduce((a, c) => a + c, 0) - discountPrice + originalEmbroideryCost + directLogo1Cost + directLogo2Cost
      return total
    }
    case 'dxm': {
      const singlePrice = 2200
      const shadowPrice = 2530
      const edgePrice = 2750
      const costsOfEmbroideries = embroideriesOfFiltered
        .map((e) => {
          const isEdge = e.edgeColor.value !== 'none' //フチ付きかどうか
          const isShadow = e.shadowColor.value !== 'none' //カゲ付きかどうか
          const cost = isEdge ? edgePrice : isShadow ? shadowPrice : singlePrice
          return cost // 2750
        }) // [2750, 2200, ...]
        .reduce((a, c) => a + c, 0) //
      return costsOfEmbroideries
    }
    case 'ragdelion': {
      const costsOfEmbroideries = embroideriesOfFiltered
        .map((e) => {
          const isEdge = e.edgeColor.value !== 'none' //フチ付きかどうか
          const isShadow = e.shadowColor.value !== 'none' //カゲ付きかどうか
          const cost = isEdge ? 3850 : isShadow ? 2750 : 1650
          return cost // 2750
        }) // [2750, 4400, ...]
        .reduce((a, c) => a + c, 0) // 7150

      const ragLionLogoCost = state.ragLionLogo.price || 0
      const ragLogoCost = state.ragLogo.price || 0
      return costsOfEmbroideries + ragLionLogoCost + ragLogoCost
    }
    // case 'treasure': {
    //   const oneMonochromaticCost = 2200
    //   const costsOfEmbroideries = embroideriesOfFiltered.map((e) => {
    //     const isEdge = e.edgeColor.value !== 'none' && e.edgeColor.value !== 'unselected' //フチ付きかどうか
    //     const isShadow = e.shadowColor.value !== 'none' && e.shadowColor.value !== 'unselected' //カゲ付きかどうか
    //     const cost = isEdge ? 3300 : isShadow ? 2750 : oneMonochromaticCost
    //     return cost
    //   })
    //   const totalBeforeDiscount = costsOfEmbroideries.reduce((a, c) => a + c, 0)
    //   const existMonochromatic = costsOfEmbroideries.includes(oneMonochromaticCost)
    //   //合算
    //   const total = existMonochromatic ? totalBeforeDiscount - oneMonochromaticCost : totalBeforeDiscount
    //   return total
    // }
    case 'gorilla': {
      const costsOfEmbroideries = embroideriesOfFiltered
        .map((e) => {
          const isEdge = e.edgeColor.value !== 'none' //フチ付きかどうか
          const isShadow = e.shadowColor.value !== 'none' //カゲ付きかどうか
          const cost = isEdge ? 3850 : isShadow ? 2970 : 2200
          return cost // 2750
        }) // [2750, 4400, ...]
        .reduce((a, c) => a + c, 0) // 7150
      return costsOfEmbroideries
    }
  }
}

export const isOrderFromKoreaChecker = (productNumber: string) => {
  const { AHN_JAE_SEOK_MODEL, SHIN_JUNG_WOO_MODEL, PARK_GE_BUM_MODEL, HAFN_DONG_HEE_MODEL } = ko
  const isOrderFromKorea = [
    AHN_JAE_SEOK_MODEL.productNumber,
    SHIN_JUNG_WOO_MODEL.productNumber,
    PARK_GE_BUM_MODEL.productNumber,
    HAFN_DONG_HEE_MODEL.productNumber
  ].includes(productNumber)
  return isOrderFromKorea
}

const brandNameResolver = (brand: Brand) => {
  switch (brand) {
    case 'atoms':
      return 'ATOMS'
    case 'yellStory':
      return 'Yell-Story'
    case 'dxm':
      return 'DxM'
    case 'ragdelion':
      return 'RAG de Lion'
    case 'gorilla':
      return 'ごりら印の野球道具'
    case 'treasure':
      return 'Treasure'
    default:
      return 'ATOMS'
  }
}

export const generateSubject = (savedId: string, brand: Brand, isJa: boolean, isEn: boolean) => {
  const brandName = brandNameResolver(brand)
  if (isJa) {
    return `【${brandName} オーダーシミュレーション】保存IDが発行されました ${savedId}`
  }
  if (isEn) {
    return `[${brandName} Order Simulation] Preservation ID has been issued. ${savedId}`
  }
  return `[${brandName} 주문 시뮬레이션] 저장 아이디가 발급되었습니다. ${savedId}`
}

export const isInvalidPersonalData = (personal: Personal): boolean => {
  if (!personal?.league || personal?.league?.length === 0) return true
  if (!personal?.position || personal?.position?.length === 0) return true
  // if (!personal?.mailAddress || personal?.mailAddress?.length === 0) return true
  return false
}
